import { useState, useEffect } from "react";
import { useConnectivity } from "@/contexts/ConnectivityContext";
import { useSyncStatus } from "@/hooks/useSyncStatus";
import { estimateStorageUsage } from "@/lib/offlineDb";
import { clearQueue } from "@/lib/syncQueue";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { WifiOff, Wifi, RefreshCw, Trash2, Bug } from "@/lib/icons";

export function OfflineDebugPanel() {
  const { isOnline, simulateOffline, setSimulateOffline, lastOnlineAt } = useConnectivity();
  const { pendingCount, lastSyncTime, isSyncing, forceSync } = useSyncStatus();
  const [storage, setStorage] = useState({ used: 0, quota: 0 });
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    estimateStorageUsage().then(setStorage);
  }, [isOpen]);

  // Only show in development
  if (import.meta.env.PROD) return null;

  const formatBytes = (bytes: number) => {
    if (bytes === 0) return "0 B";
    const k = 1024;
    const sizes = ["B", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
  };

  if (!isOpen) {
    return (
      <button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-20 right-3 z-50 h-8 w-8 rounded-full bg-card border shadow-md flex items-center justify-center text-muted-foreground hover:text-foreground transition-colors lg:bottom-4"
        title="Offline Debug"
      >
        <Bug className="h-3.5 w-3.5" />
      </button>
    );
  }

  return (
    <div className="fixed bottom-20 right-3 z-50 w-72 bg-card border rounded-xl shadow-xl p-4 space-y-3 lg:bottom-4">
      <div className="flex items-center justify-between">
        <h3 className="text-xs font-semibold text-foreground flex items-center gap-1.5">
          <Bug className="h-3.5 w-3.5" /> Offline Debug
        </h3>
        <button onClick={() => setIsOpen(false)} className="text-muted-foreground hover:text-foreground text-xs">✕</button>
      </div>

      <div className="space-y-2 text-xs">
        <div className="flex items-center justify-between">
          <span className="text-muted-foreground">Status:</span>
          <span className={`font-medium flex items-center gap-1 ${isOnline ? "text-primary" : "text-amber-500"}`}>
            {isOnline ? <Wifi className="h-3 w-3" /> : <WifiOff className="h-3 w-3" />}
            {isOnline ? "Online" : "Offline"}
          </span>
        </div>

        <div className="flex items-center justify-between">
          <span className="text-muted-foreground">Queued mutations:</span>
          <span className="font-medium text-foreground">{pendingCount}</span>
        </div>

        <div className="flex items-center justify-between">
          <span className="text-muted-foreground">Last sync:</span>
          <span className="font-medium text-foreground">
            {lastSyncTime ? new Date(lastSyncTime).toLocaleTimeString() : "Never"}
          </span>
        </div>

        <div className="flex items-center justify-between">
          <span className="text-muted-foreground">Storage:</span>
          <span className="font-medium text-foreground">{formatBytes(storage.used)}</span>
        </div>

        <div className="flex items-center justify-between pt-1 border-t">
          <Label htmlFor="simulate-offline" className="text-xs text-muted-foreground">
            Simulate offline
          </Label>
          <Switch
            id="simulate-offline"
            checked={simulateOffline}
            onCheckedChange={setSimulateOffline}
          />
        </div>
      </div>

      <div className="flex gap-2">
        <Button size="sm" variant="outline" className="flex-1 text-xs h-7" onClick={forceSync} disabled={isSyncing}>
          <RefreshCw className={`h-3 w-3 mr-1 ${isSyncing ? "animate-spin" : ""}`} />
          Sync
        </Button>
        <Button size="sm" variant="outline" className="flex-1 text-xs h-7" onClick={clearQueue}>
          <Trash2 className="h-3 w-3 mr-1" />
          Clear queue
        </Button>
      </div>
    </div>
  );
}
